import { useState } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Button, IconButton, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { clsx } from 'clsx';

import type { ParsedToken } from '../../../types/QueryBuilder';
import { useQueryBuilderContext } from '../context';
import { useFilterBarContext } from '../FilterBarContext';
import { useQueryBuilderGridItem } from '../hooks/useQueryBuilderGridItem';
import { replaceToken } from '../utils';

import { LogicOperatorInput, useLogicOperatorEditing } from './LogicOperatorInput';

import styles from './LogicBoolToken.module.css';

type LogicBoolParsedToken = Extract<ParsedToken, { type: 'LOGIC_BOOL' }>;

interface LogicBoolTokenProps {
  tokenKey: string;
  token: LogicBoolParsedToken;
  onChange: (value: 'AND' | 'OR') => void;
  onRemove: () => void;
  removable?: boolean;
}

export function LogicBoolToken({ tokenKey, token, onChange, onRemove, removable = true }: LogicBoolTokenProps) {
  const { dispatch, query } = useQueryBuilderContext();
  const { readonly, gridState } = useFilterBarContext();
  const { containerProps, cellProps } = useQueryBuilderGridItem(tokenKey, gridState);
  const [activePart, setActivePart] = useState<'operator' | 'remove' | null>(null);
  const editing = useLogicOperatorEditing(tokenKey, token, onChange);

  function handlePress() {
    const newValue = token.value === 'AND' ? 'OR' : 'AND';
    dispatch({ type: 'SET_QUERY', query: replaceToken(query, token, newValue) });
    onChange(newValue);
  }

  function renderOperator() {
    if (readonly) {
      return (
        <span
          data-part="operator"
          className={styles.label}
          aria-label={msg('queryBuilder.logicOperator.label', { operator: token.value })}
        >
          {token.value}
        </span>
      );
    }
    const control = editing.isEditing ? (
      <LogicOperatorInput
        tokenKey={tokenKey}
        operator={token.value}
        value={editing.value}
        onInput={editing.handleInput}
        onStop={editing.stopEditing}
      />
    ) : (
      <Button
        data-part="operator"
        className={clsx(styles.cell, {
          [styles.cellFocused]: activePart === 'operator',
          [styles.cellFocusedPrev]: activePart === 'remove',
        })}
        onFocus={() => setActivePart('operator')}
        onBlur={() => setActivePart(null)}
        onPress={handlePress}
        aria-label={msg('queryBuilder.logicOperator.toggleLabel', { operator: token.value })}
      >
        {token.value}
      </Button>
    );
    return (
      <TooltipTrigger delay={500} isDisabled={editing.isEditing}>
        {control}
        <Tooltip>
          <Message
            id="queryBuilder.logicOperator.toggleTooltip"
            values={{ operator: token.value === 'AND' ? 'OR' : 'AND' }}
          />
        </Tooltip>
      </TooltipTrigger>
    );
  }

  return (
    // oxlint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- wrapper exists only to stop click propagation; interactive controls live inside
    <div className={styles.container} onClick={(e) => e.stopPropagation()}>
      <div {...containerProps} className={styles.logicBoolToken}>
        {/* Operator cell */}
        <div {...cellProps}>
          <span className={styles.editableCell}>
            <span aria-hidden="true" className={styles.editableCellOverlay} />
            {renderOperator()}
          </span>
        </div>

        {/* Remove cell */}
        {removable && (
          <div {...cellProps}>
            <TooltipTrigger>
              <IconButton
                className={clsx(styles.removeButton, {
                  [styles.cellFocusedNext]: activePart === 'operator',
                })}
                onFocus={() => setActivePart('remove')}
                onBlur={() => setActivePart(null)}
                icon="cancel"
                variant="minimal"
                size="small"
                aria-label={msg('queryBuilder.logicOperator.removeLabel')}
                onPress={onRemove}
                isDisabled={readonly}
              />
              <Tooltip>
                <Message id="queryBuilder.logicOperator.removeTooltip" />
              </Tooltip>
            </TooltipTrigger>
          </div>
        )}
      </div>
    </div>
  );
}

LogicBoolToken.displayName = 'LogicBoolToken';
